<script setup lang="ts">
import { defineAsyncComponent, onBeforeUnmount, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

import { ForbiddenError } from '@/api/errors.ts';
import { startStream } from '@/composables/NotificationStream';
import { errorMessage } from '@/composables/Notify.ts';
import { captureError } from '@/composables/Sentry.ts';
import { useLanguages } from '@/composables/useLanguages.ts';

import { useAuthStore } from '@/stores/AuthStore.ts';
import { useStylesStore } from '@/stores/ui/StylesStore.ts';
import { useChatStore } from '@/stores/v2/ChatStore.ts';

import { useScoutForDialogs } from '@/features/assistant/composables/useScoutForDialogs.ts';

import Footer from '@/layouts/components/DefaultFooter.vue';
import Header from '@/layouts/components/header/DefaultHeader.vue';
import Drawer from '@/layouts/components/sidebar/DefaultSidebar.vue';

const { locale } = useI18n();
const store = useAuthStore();
const stylesStore = useStylesStore();
const chatStore = useChatStore();
const { setUILanguage } = useLanguages();
useScoutForDialogs();

// Lazy: the chat's TipTap chain reaches stores at module load, before Pinia is installed.
const ScoutChat = defineAsyncComponent(() => import('@/features/assistant/ScoutChat.vue'));

const ctrl = new AbortController();

function openMobileDrawer() {
  stylesStore.showDrawer(true);
}

onMounted(async () => {
  try {
    const user = store.user;

    if (!user) return;

    setUILanguage(user, locale);
    await store.userLastAccess();
  } catch (error) {
    if (error instanceof ForbiddenError) {
      errorMessage(error);

      return;
    }

    errorMessage(error);

    captureError(error, {
      message: 'Component: DefaultLayout, Hook: onMounted',
    });
  }

  try {
    startStream();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    if (e.response && e.response.headers.get('Content-Type') === 'application/json') {
      return;
    }

    captureError(e, {
      message: 'Component: DefaultLayout, Function: startStream',
    });
  }
});

onBeforeUnmount(() => {
  ctrl.abort();
});
</script>

<template>
  <q-layout view="lHr lpR lfr">
    <Header :style="{ left: `${stylesStore.drawerWidth}px` }" @toggle-drawer="openMobileDrawer" />
    <Drawer />
    <q-page-container :style="{ paddingLeft: `${stylesStore.drawerWidth}px` }">
      <router-view />
    </q-page-container>
    <Footer />
    <ScoutChat v-if="chatStore.scoutAvailable" />
  </q-layout>
</template>

<style lang="scss">
// Do not make scoped (styles bug).

.q-layout {
  background: $secondary-50;
  :deep(.q-page-container) {
    background: transparent !important;
    padding: 0 !important;
  }
}
</style>
